import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import { Input, Button } from '../shared/components/ui'
import { PasswordStrengthIndicator } from '../components/PasswordStrengthIndicator'
import { supabase } from '../lib/supabase'
import { apiRequestProtected } from '../utils/axios'
import { LocalStorageItem } from '../common/constants'
import { useAuthContext } from '../contexts/AuthContext'
import logo from '../logo.jpg'

interface ProfileFormData {
  firstName: string
  lastName: string
  password: string
  confirmPassword: string
}

interface FormErrors {
  firstName?: string
  lastName?: string
  password?: string
  confirmPassword?: string
  submit?: string
}

function validateForm(formData: ProfileFormData): FormErrors {
  const errors: FormErrors = {}

  if (!formData.firstName.trim()) {
    errors.firstName = 'First name is required'
  } else if (formData.firstName.length < 2) {
    errors.firstName = 'First name must be at least 2 characters'
  }

  if (!formData.lastName.trim()) {
    errors.lastName = 'Last name is required'
  } else if (formData.lastName.length < 2) {
    errors.lastName = 'Last name must be at least 2 characters'
  }

  if (!formData.password) {
    errors.password = 'Password is required'
  } else {
    const passwordRequirements = [
      { met: formData.password.length >= 8, message: 'Password must be at least 8 characters' },
      { met: /[a-z]/.test(formData.password), message: 'Password must contain a lowercase letter' },
      { met: /\d/.test(formData.password), message: 'Password must contain a number' },
    ]

    const unmetRequirement = passwordRequirements.find((req) => !req.met)
    if (unmetRequirement) {
      errors.password = unmetRequirement.message
    }
  }

  if (!formData.confirmPassword) {
    errors.confirmPassword = 'Please confirm your password'
  } else if (formData.password !== formData.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match'
  }

  return errors
}

export function CompleteProfilePage() {
  const navigate = useNavigate()
  const { completeSignIn } = useAuthContext()
  const [email, setEmail] = useState('')
  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const isFormValid = (): boolean => {
    if (!formData.firstName.trim() || formData.firstName.length < 2) return false
    if (!formData.lastName.trim() || formData.lastName.length < 2) return false
    if (!formData.password) return false
    if (!formData.confirmPassword) return false

    const passwordValid =
      formData.password.length >= 8 &&
      /[a-z]/.test(formData.password) &&
      /\d/.test(formData.password)

    if (!passwordValid) return false

    if (formData.password !== formData.confirmPassword) return false

    return true
  }

  useEffect(() => {
    const checkAuth = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (session?.user?.email) {
        console.log('✅ Active Supabase session found')
        setEmail(session.user.email)
        return
      }

      console.error('❌ No active session, redirecting to login')
      navigate('/login', {
        replace: true,
        state: { message: 'Please use the link from your invitation email.' },
      })
    }

    checkAuth()
  }, [navigate])

  function handleInputChange(field: keyof ProfileFormData, value: string) {
    const newFormData = { ...formData, [field]: value }
    setFormData(newFormData)

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }

    if (field === 'confirmPassword' && value) {
      if (newFormData.password !== value) {
        setErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match' }))
      } else {
        setErrors((prev) => ({ ...prev, confirmPassword: undefined }))
      }
    }

    if (field === 'password' && newFormData.confirmPassword) {
      if (value !== newFormData.confirmPassword) {
        setErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match' }))
      } else {
        setErrors((prev) => ({ ...prev, confirmPassword: undefined }))
      }
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const validationErrors = validateForm(formData)
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    setSubmitting(true)
    setErrors({})

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session) {
        throw new Error('No active session. Please use the link from your invitation email.')
      }

      const { error: passwordError } = await supabase.auth.updateUser({
        password: formData.password,
      })

      if (passwordError) {
        console.error('❌ Failed to set password:', passwordError)
        throw new Error(passwordError.message || 'Failed to set password')
      }

      console.log('✅ Password set successfully')

      const response = await apiRequestProtected('POST', '/auth/finalize-registration', {
        firstName: formData.firstName,
        lastName: formData.lastName,
      })

      if (!response.success) {
        throw new Error(response.error?.message || 'Registration failed')
      }

      console.log('✅ Registration completed successfully!')

      localStorage.removeItem(LocalStorageItem.registrationPending)
      if (session.user.email) {
        completeSignIn(session.user.email, session.access_token)
      }

      navigate('/map', { replace: true })
    } catch (error: any) {
      console.error('❌ Registration finalization failed:', error)

      if (error?.error?.message) {
        setErrors({ submit: error.error.message })
      } else if (error?.message) {
        setErrors({ submit: error.message })
      } else {
        setErrors({ submit: 'Failed to complete registration. Please try again.' })
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (!email) {
    return null
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 py-12 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <div className="mb-2 flex justify-center">
              <img src={logo} alt="IoAg" className="h-16" />
            </div>
            <p className="text-gray-600 text-sm">IoT Agriculture Monitoring</p>
          </div>

          <h2 className="text-2xl font-semibold text-center text-gray-900 mb-2">Complete Your Profile</h2>
          <p className="text-center text-gray-600 text-sm mb-6">Set up your account to get started</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                value={email}
                disabled
                className="w-full bg-gray-50"
              />
            </div>

            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <Input
                id="firstName"
                name="firstName"
                type="text"
                autoComplete="given-name"
                placeholder="Enter your first name"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                error={!!errors.firstName}
                className="w-full"
                autoFocus
                disabled={submitting}
                required
              />
              {errors.firstName && <p className="text-xs text-red-600 mt-1">{errors.firstName}</p>}
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <Input
                id="lastName"
                name="lastName"
                type="text"
                autoComplete="family-name"
                placeholder="Enter your last name"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                error={!!errors.lastName}
                className="w-full"
                disabled={submitting}
                required
              />
              {errors.lastName && <p className="text-xs text-red-600 mt-1">{errors.lastName}</p>}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Create a strong password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  error={!!errors.password}
                  className="w-full pr-10"
                  disabled={submitting}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-600 mt-1">{errors.password}</p>}
            </div>

            {formData.password && (
              <div className="bg-gray-50 rounded-lg p-4">
                <PasswordStrengthIndicator password={formData.password} />
              </div>
            )}

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Re-enter your password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  error={!!errors.confirmPassword}
                  className="w-full pr-10"
                  disabled={submitting}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {errors.confirmPassword && <p className="text-xs text-red-600 mt-1">{errors.confirmPassword}</p>}
            </div>

            {errors.submit && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                {errors.submit}
              </div>
            )}

            <div className="pt-2">
              <Button
                type="submit"
                disabled={submitting || !isFormValid()}
                loading={submitting}
                variant="primary"
                className="w-full"
              >
                {submitting ? 'Completing Registration...' : 'Complete Registration'}
              </Button>
            </div>
          </form>
        </div>

        <div className="text-center mt-4">
          <p className="text-sm text-gray-600">
            Need help?{' '}
            <Link to="/contact" className="text-blue-600 hover:text-blue-800 hover:underline">
              Contact Support
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
